import {
  shortOrderRef,
  type MarketplaceFulfillmentStatus,
} from "@nexus/contracts";
import {
  Badge,
  Button,
  DatePicker,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  type IconName,
  Sheet,
  SheetContent,
  SheetTitle,
  Skeleton,
  Tabs,
  TabsList,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { useQueries } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  useCallback,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  orderScriptPreflightCommentsQueryOptions,
  useAddOrderScriptPreflightComment,
  useSetOrderNoteReaction,
  useUpdateOrderDueDate,
} from "@/lib/queries";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";

import { ClientMark } from "../../partner/components/ClientMark";
import { countRevisionRequests } from "../../lib/order-activity";
import type { MarketplaceFulfillmentViewer } from "../../lib/fulfillment-badge-variant";
import {
  describeDeadline,
  formatYmdLong,
  formatYmdShort,
  parseYmdToLocalDate,
  toLocalYmd,
} from "../../lib/order-deadline";
import {
  canShowOrderLineReview,
  isMultiHookLine,
} from "../../lib/order-line-hooks";
import {
  canDuplicateOrderLine,
  resolveOrderLineType,
} from "../../lib/order-line-kind";
import {
  describeOrderScript,
  resolveOpportunitySetName,
} from "../../lib/order-script-display";
import { OrderLineHookVideos } from "./OrderLineHookVideos";
import { OrderPersonaBriefSection } from "./OrderPersonaBriefSection";
import { OrderScriptStoryboards } from "./OrderScriptStoryboards";
import {
  adjacentScriptTarget,
  clampPanelWidth,
  persistPanelWidth,
  type OrdersPanelTarget,
} from "../../lib/orders-detail-panel";
import {
  fulfillmentOptionsFor,
  fulfillmentOptionsForOpsAdmin,
  fulfillmentOptionsForOpsAdminScript,
  isOpenDeadlineScript,
  openOrderDeadlineYmd,
} from "../../lib/orders-list-model";
import type {
  MarketplaceOrder,
  MarketplaceOrderTenant,
  OrderScriptPreflightComment,
  OrderScriptRow,
} from "../../types/marketplace.types";
import { OrderScriptVideoActivitySections } from "../OrderScriptVideoActivitySections";
import { OrderScriptRowActions } from "../OrderScriptRowActions";
import { ScriptReviewAction } from "../ScriptReviewAction";
import { ClampedTitle } from "./ClampedTitle";
import { CopyableId } from "./CopyableId";
import {
  DeadlineChip,
  dueDatePickerIcon,
  dueDatePickerTriggerClass,
} from "./DeadlineChip";
import { ExtendDueMenu } from "./ExtendDueMenu";
import { OpportunitySetLink } from "./OpportunitySetLink";
import { OrderTypeBadge } from "./OrderTypeBadge";
import { DetailCommentComposer } from "./DetailCommentComposer";
import { DetailMetaGrid } from "./DetailMetaGrid";
import { buildOrderTargetRatioMetaItems } from "./OrderTargetRatioMeta";
import { PersonaIdentity } from "./PersonaIdentity";
import { formatMarketplaceSentAt } from "../../lib/format-marketplace-timestamp";
import { SentByIdentity } from "./SentByIdentity";
import { ScriptActionButtons } from "./ScriptActionButtons";
import { StatusMenu } from "./StatusMenu";
import {
  canShowVendorBriefActions,
  VendorBriefActions,
} from "./vendor-brief/VendorBriefActions";
import { VendorBriefSection } from "./VendorBriefSection";
import { VendorIdentity } from "./VendorIdentity";

interface OrdersDetailPanelProps {
  target: OrdersPanelTarget;
  order: MarketplaceOrder;
  script: OrderScriptRow | null;
  todayYmd: string;
  tenantId: string;
  tenant?: MarketplaceOrderTenant;
  variant: "in-production" | "history" | "archive";
  canEdit: boolean;
  canEditDue?: boolean;
  statusOptionMode?: "workflow" | "ops-admin";
  canViewActivity: boolean;
  canAddComment?: boolean;
  activityAudience?: "admin" | "client";
  activityViewer?: MarketplaceFulfillmentViewer;
  width: number;
  /** Hub edit drawers are a fixed wide sheet. Marketplace keeps the resizable width. */
  layout?: "resizable" | "hub";
  pendingScriptIds: ReadonlySet<string>;
  onWidthChange: (width: number) => void;
  onClose: () => void;
  onTargetChange: (target: OrdersPanelTarget) => void;
  onStatusChange: (
    scriptId: string,
    next: MarketplaceFulfillmentStatus,
    previous: MarketplaceFulfillmentStatus,
  ) => void;
  onDownloadAsset?: (script: OrderScriptRow) => Promise<void>;
  onViewHistory: () => void;
  showViewHistoryAction?: boolean;
  showCopyLinkAction?: boolean;
  /** Ops tracker only; omit on client and hub surfaces. */
  onDuplicateScript?: (script: OrderScriptRow) => void;
  duplicatePending?: boolean;
  /** Ops tracker only; omit on client and hub surfaces. */
  onSendToPartner?: (script: OrderScriptRow) => void;
}

function dueMetaLabel(
  ymd: string | null,
  todayYmd: string,
  locale: string,
  overdue: (count: number) => string,
): string | null {
  if (!ymd) return null;
  const info = describeDeadline(ymd, todayYmd);
  const absolute = formatYmdLong(ymd, locale);
  if (!info || info.tone !== "overdue") return absolute;
  return `${absolute} · ${overdue(Math.abs(info.daysFromToday))}`;
}

function ActivitySkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-hidden>
      <Skeleton className="h-4 w-28" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
    </div>
  );
}

function PanelIconTooltip({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function HubOrderDrawerHeader({
  title,
  showCopyLinkAction,
  onCopyLink,
  showOpenOrder,
  onOpenOrder,
  viewHistory,
  briefActions,
  reviewAction,
  meta,
}: {
  title: ReactNode;
  showCopyLinkAction: boolean;
  onCopyLink: () => void;
  showOpenOrder: boolean;
  onOpenOrder: () => void;
  viewHistory: { label: string; icon: IconName; onSelect: () => void } | null;
  briefActions: ReactNode;
  reviewAction: ReactNode;
  meta: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex min-h-16 items-center gap-2 pl-14">
        <SheetTitle className="min-w-0 truncate text-drawer-title">
          {title}
        </SheetTitle>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {briefActions}
          {showOpenOrder ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              iconStart="file-text"
              onClick={onOpenOrder}
            >
              {t("marketplace.orders.list.panel.openOrder")}
            </Button>
          ) : null}
          {viewHistory ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              iconStart={viewHistory.icon}
              onClick={viewHistory.onSelect}
            >
              {viewHistory.label}
            </Button>
          ) : null}
          {showCopyLinkAction ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              iconStart="link"
              onClick={onCopyLink}
            >
              {t("marketplace.orders.list.panel.copyLink")}
            </Button>
          ) : null}
          {reviewAction}
        </div>
      </div>
      <div className="flex min-w-0 flex-nowrap items-center gap-2 overflow-hidden pb-4 pl-14">
        {meta}
      </div>
    </>
  );
}

function ContentSection({
  title,
  children,
  wide = false,
  className,
}: {
  title: string;
  children: string;
  wide?: boolean;
  className?: string;
}) {
  return (
    <section className={cn("flex flex-col gap-1", className)}>
      <h3 className="text-caption tracking-label font-medium uppercase text-muted-foreground">
        {title}
      </h3>
      <p
        className={cn(
          "text-drawer-body whitespace-pre-wrap text-foreground",
          wide ? "max-w-none" : "max-w-[70ch]",
        )}
      >
        {children}
      </p>
    </section>
  );
}

export function OrdersDetailPanel({
  target,
  order,
  script,
  todayYmd,
  tenantId,
  tenant,
  variant,
  canEdit,
  canEditDue = canEdit,
  statusOptionMode = "workflow",
  canViewActivity,
  canAddComment = canViewActivity,
  activityAudience = "admin",
  activityViewer = "client",
  width,
  layout = "resizable",
  pendingScriptIds,
  onWidthChange,
  onClose,
  onTargetChange,
  onStatusChange,
  onDownloadAsset,
  onViewHistory,
  showViewHistoryAction = true,
  showCopyLinkAction = true,
  onDuplicateScript,
  duplicatePending = false,
  onSendToPartner,
}: OrdersDetailPanelProps) {
  const { t, i18n } = useTranslation();
  const widthRef = useRef(width);
  widthRef.current = width;
  const addComment = useAddOrderScriptPreflightComment(activityAudience);
  const noteReaction = useSetOrderNoteReaction(activityAudience);
  const [activeTab, setActiveTab] = useState<"content" | "activity">("content");
  const updateDueDate = useUpdateOrderDueDate();
  const statusOptionsFor =
    statusOptionMode === "ops-admin"
      ? fulfillmentOptionsForOpsAdmin
      : fulfillmentOptionsFor;
  const { copy } = useCopyToClipboard({
    onCopy: () => toast.success(t("marketplace.orders.list.panel.linkCopied")),
  });
  const isHub = layout === "hub";
  const isScript = target.kind === "script" && script != null;
  const activeScript = isScript ? script : null;
  const canViewHistory = showViewHistoryAction && variant === "in-production";
  const isArchivedOrder = order.fulfillmentStatus === "CANCELLED";
  const viewHistoryIcon: IconName = isArchivedOrder ? "file" : "history";
  const viewHistoryLabel = isArchivedOrder
    ? t("marketplace.orders.list.panel.viewArchive", "View in Archive")
    : t("marketplace.orders.list.panel.viewHistory", "View in History");
  // TODO(alex): enable brief-line Review for clients once brief-mode preflight ships (AD-2777)
  const canReviewBriefLines = activityAudience === "admin";
  const showBriefActions =
    !activeScript && canShowVendorBriefActions(order.handoff, activityViewer);
  const isReusableActiveScript =
    activeScript != null && canDuplicateOrderLine(activeScript);
  const canDuplicateActiveScript = onDuplicateScript != null && isReusableActiveScript;
  const canSendActiveScript = onSendToPartner != null && isReusableActiveScript;
  const hasPanelMenuActions =
    !isHub && (isScript || canViewHistory || showCopyLinkAction);
  const hasMoreActions = hasPanelMenuActions || showBriefActions;
  const display = activeScript
    ? describeOrderScript(
        activeScript,
        t("marketplace.orders.detail.unnamedScript"),
      )
    : null;

  const scriptIds =
    target.kind === "order"
      ? order.scripts.map((row) => row.id)
      : activeScript
        ? [activeScript.id]
        : [];

  const activityQueries = useQueries({
    queries: scriptIds.map((scriptId) => ({
      ...orderScriptPreflightCommentsQueryOptions(order.id, scriptId, {
        audience: activityAudience,
        tenantId,
      }),
      enabled: canViewActivity,
    })),
  });

  const activity: OrderScriptPreflightComment[] = activityQueries.flatMap(
    (query) => query.data ?? [],
  );
  const activityLoading = activityQueries.some((query) => query.isLoading);
  const activityError = activityQueries.some((query) => query.isError);

  const deadlineScripts = order.scripts.filter(
    (row) => row.fulfillmentStatus !== "CANCELLED",
  );
  const dueYmd = isScript
    ? activeScript?.fulfillmentStatus === "CANCELLED"
      ? null
      : (activeScript?.expectedDeliveryDate ?? order.etaDate)
    : deadlineScripts.length === 0
      ? null
      : ([
        ...deadlineScripts
          .map((row) => row.expectedDeliveryDate)
          .filter((value): value is string => Boolean(value)),
        ...(order.etaDate ? [order.etaDate] : []),
      ].sort()[0] ?? null);
  const extendableDueYmd = activeScript
    ? isOpenDeadlineScript(activeScript)
      ? (activeScript.expectedDeliveryDate ?? order.etaDate)
      : null
    : openOrderDeadlineYmd(order);
  const sentIso = order.sentAt ?? order.createdAt;
  const sentLabel = formatMarketplaceSentAt(sentIso, i18n.language);
  const sentByName = order.sentByUserName?.trim() || null;
  const sentByValue = sentByName ? (
    <SentByIdentity name={sentByName} userId={order.sentByUserId} />
  ) : null;

  const title = display?.title ?? t("marketplace.orders.detail.unnamedScript");

  const handleResizePointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    const startX = event.clientX;
    const startWidth = widthRef.current;
    const onMove = (moveEvent: PointerEvent) => {
      onWidthChange(clampPanelWidth(startWidth + (startX - moveEvent.clientX)));
    };
    const onUp = () => {
      handle.removeEventListener("pointermove", onMove);
      persistPanelWidth(widthRef.current);
    };
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp, { once: true });
  };

  const copyLink = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("tab", variant);
    url.searchParams.set("order", order.id);
    if (activeScript) url.searchParams.set("script", activeScript.id);
    else url.searchParams.delete("script");
    void copy(url.toString());
  }, [activeScript, copy, order.id, variant]);

  const handleStatus = (
    scriptId: string,
    next: MarketplaceFulfillmentStatus,
  ) => {
    const current = order.scripts.find(
      (row) => row.id === scriptId,
    )?.fulfillmentStatus;
    if (!current || current === next) return;
    onStatusChange(scriptId, next, current);
    toast.success(t("marketplace.orders.list.panel.statusUpdated"), {
      action: {
        label: t("marketplace.orders.list.bulk.undo"),
        onClick: () => onStatusChange(scriptId, current, next),
      },
    });
  };

  const dueDateTarget = {
    orderId: order.id,
    tenantId,
    orderScriptId: activeScript?.id ?? null,
  };

  const handleDueDateChange = (date: Date | undefined) => {
    updateDueDate.mutate({
      ...dueDateTarget,
      dueDate: date ? toLocalYmd(date) : null,
    });
  };

  const handleExtendDue = (nextDueYmd: string) => {
    updateDueDate.mutate(
      { ...dueDateTarget, dueDate: nextDueYmd },
      {
        onSuccess: () =>
          toast.success(
            t("marketplace.orders.extend.success", {
              date: formatYmdLong(nextDueYmd, i18n.language),
            }),
          ),
      },
    );
  };

  const dueCombined = dueMetaLabel(dueYmd, todayYmd, i18n.language, (count) =>
    t("marketplace.orders.list.deadline.overdue", { count }),
  );
  const deadline = describeDeadline(dueYmd, todayYmd);
  const dueDisplayLabel = !deadline
    ? t("marketplace.orders.list.deadline.none")
    : deadline.tone === "overdue"
      ? t("marketplace.orders.list.deadline.overdue", {
          count: Math.abs(deadline.daysFromToday),
        })
      : deadline.tone === "today"
        ? t("marketplace.orders.list.deadline.today")
        : deadline.tone === "soon"
          ? t("marketplace.orders.list.deadline.soon", {
              count: deadline.daysFromToday,
            })
          : t("marketplace.orders.list.deadline.scheduled", {
              date: formatYmdShort(deadline.ymd, todayYmd, i18n.language),
            });
  const dueAbsolute = dueYmd ? formatYmdLong(dueYmd, i18n.language) : null;
  const dueAbsoluteTooltip = dueAbsolute
    ? t("marketplace.orders.list.deadline.tooltip", {
        date: dueAbsolute,
      })
    : t("marketplace.orders.list.deadline.noneTooltip");
  const duePicker = canEditDue ? (
    <DatePicker
      value={dueYmd ? (parseYmdToLocalDate(dueYmd) ?? undefined) : undefined}
      onChange={handleDueDateChange}
      displayLabel={dueDisplayLabel}
      disabled={updateDueDate.isPending}
      triggerIcon={dueDatePickerIcon(deadline?.tone)}
      triggerIconColor="inherit"
      labels={{
        placeholder: t("marketplace.orders.eta.datePlaceholder"),
        triggerAria: t("marketplace.orders.list.panel.editDue"),
        clear: t("marketplace.orders.eta.dateClear"),
        today: t("marketplace.orders.eta.dateToday"),
      }}
      triggerClassName={dueDatePickerTriggerClass(deadline?.tone)}
    />
  ) : null;
  const dueField = duePicker ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex min-w-0">{duePicker}</span>
      </TooltipTrigger>
      <TooltipContent>{dueAbsoluteTooltip}</TooltipContent>
    </Tooltip>
  ) : (
    <DeadlineChip ymd={dueYmd} todayYmd={todayYmd} />
  );
  const dueEditor =
    canEditDue &&
    extendableDueYmd &&
    describeDeadline(extendableDueYmd, todayYmd)?.tone === "overdue" ? (
      <span className="inline-flex min-w-0 items-center gap-1.5">
        {dueField}
        <ExtendDueMenu
          dueYmd={extendableDueYmd}
          todayYmd={todayYmd}
          pending={updateDueDate.isPending}
          size="xs"
          onExtend={handleExtendDue}
        />
      </span>
    ) : (
      dueField
    );
  const revisionCount = activeScript ? countRevisionRequests(activity) : 0;
  const scriptIndex = activeScript
    ? order.scripts.findIndex((row) => row.id === activeScript.id)
    : -1;

  const previousTarget = adjacentScriptTarget(target, order.scripts, -1);
  const nextTarget = adjacentScriptTarget(target, order.scripts, 1);
  const opportunityContext = activeScript?.script?.opportunity;
  const sourceText = activeScript
    ? display?.briefOnly
      ? t("marketplace.orders.list.panel.sourceImported")
      : activeScript.script?.source === "persona"
        ? t("marketplace.orders.list.panel.sourcePersona")
        : t("marketplace.orders.list.panel.sourceOpportunity")
    : null;
  const linkedPersona = opportunityContext?.persona ?? null;
  const sourceFieldLabel = linkedPersona
    ? t("marketplace.orders.list.panel.persona")
    : t("marketplace.orders.list.panel.source");
  const sourceLabel = linkedPersona ? (
    <PersonaIdentity
      id={linkedPersona.id}
      title={linkedPersona.title}
      tenantId={tenantId}
    />
  ) : (
    sourceText
  );
  const opportunitySetId = opportunityContext?.setId?.trim() || null;
  const opportunitySetName = resolveOpportunitySetName(activeScript);
  const opportunitySetLink = opportunitySetId ? (
    <OpportunitySetLink
      setId={opportunitySetId}
      setName={opportunitySetName}
    />
  ) : null;
  const opportunityLabel =
    display?.opportunitySummary ??
    (activeScript?.script?.source !== "persona" &&
    activeScript?.script?.opportunityId != null &&
    activeScript.script.opportunityId >= 0
      ? t("marketplace.orders.list.panel.opportunityNumber", {
          id: activeScript.script.opportunityId,
        })
      : null);
  const activitySection = canViewActivity ? (
    <section className="flex flex-col gap-3">
      {activityLoading ? (
        <ActivitySkeleton />
      ) : activityError ? (
        <p role="alert" className="text-drawer-body text-error">
          {t("marketplace.orders.list.panel.activityError")}
        </p>
      ) : activity.length > 0 ? (
        <OrderScriptVideoActivitySections
          script={activeScript!}
          entries={activity}
          viewer={activityViewer}
          showFilters={Boolean(activeScript)}
          defaultFilter="all"
          collapseSystemRuns={Boolean(activeScript)}
          onReactionChange={(entry, reactionType, active) =>
            noteReaction.setReaction({
              tenantId,
              orderId: order.id,
              orderScriptId: entry.orderScriptId,
              commentId: entry.id,
              reactionType,
              active,
            })
          }
          isReactionPending={(entry, reactionType) =>
            noteReaction.isPending({
              tenantId,
              orderId: order.id,
              orderScriptId: entry.orderScriptId,
              commentId: entry.id,
              reactionType,
              active:
                entry.reactions?.find(
                  (group) => group.reactionType === reactionType,
                )?.isCurrentPersonActive ?? false,
            })
          }
        />
      ) : (
        <div className="flex flex-col gap-2">
          <h3 className="text-drawer-body font-medium text-foreground">
            {t("marketplace.orders.activity.headingWithCount", {
              count: 0,
            })}
          </h3>
          <p className="text-drawer-body text-muted-foreground">
            {t("marketplace.orders.list.panel.activityEmpty")}
          </p>
        </div>
      )}
    </section>
  ) : null;

  return (
    <Sheet
      open
      modal={isHub ? false : undefined}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <SheetContent
        data-orders-panel
        side="right"
        showClose={isHub}
        closePosition={isHub ? "left" : "right"}
        closeIcon={isHub ? "chevron-right" : "x"}
        closeClassName={isHub ? "top-[18px]" : undefined}
        aria-describedby={undefined}
        onPointerDownOutside={isHub ? (event) => event.preventDefault() : undefined}
        onInteractOutside={isHub ? (event) => event.preventDefault() : undefined}
        onEscapeKeyDown={isHub ? (event) => event.preventDefault() : undefined}
        className={cn(
          "flex h-full w-full flex-col overflow-hidden p-0",
          layout === "hub"
            ? "sm:max-w-[1100px] 2xl:max-w-[1300px]"
            : "max-w-[min(var(--orders-panel-width),100vw)]",
        )}
        style={
          layout === "hub"
            ? undefined
            : ({ "--orders-panel-width": `${width}px` } as CSSProperties)
        }
      >
        {layout === "hub" ? null : (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t("marketplace.orders.list.panel.resize")}
          tabIndex={-1}
          className="absolute inset-y-0 left-0 z-10 w-1.5 cursor-col-resize touch-none hover:bg-primary/20"
          onPointerDown={handleResizePointerDown}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") {
              event.preventDefault();
              onWidthChange(clampPanelWidth(width + 16));
            }
            if (event.key === "ArrowRight") {
              event.preventDefault();
              onWidthChange(clampPanelWidth(width - 16));
            }
          }}
        />
        )}

        <header
          className={cn(
            "sticky top-0 z-10 flex flex-col border-b border-border bg-background px-6",
            isHub ? "gap-0" : "gap-3",
            isHub ? undefined : canViewActivity ? "pt-4" : "py-4",
          )}
        >
          {isHub ? (
            <HubOrderDrawerHeader
              title={
                activeScript ? (
                  title
                ) : (
                  <>
                    {t("marketplace.orders.list.panel.orderNoun")}{" "}
                    <CopyableId
                      value={order.id}
                      displayValue={shortOrderRef(order.id) ?? order.id}
                      density="quiet"
                      copyLabel={t("marketplace.orders.copyOrderId")}
                      copiedMessage={t("marketplace.orders.copyOrderIdSuccess")}
                      className="text-drawer-title text-foreground"
                    />
                  </>
                )
              }
              showCopyLinkAction={showCopyLinkAction}
              onCopyLink={copyLink}
              showOpenOrder={isScript}
              onOpenOrder={() =>
                onTargetChange({ kind: "order", orderId: order.id })
              }
              viewHistory={
                canViewHistory
                  ? {
                      label: viewHistoryLabel,
                      icon: viewHistoryIcon,
                      onSelect: onViewHistory,
                    }
                  : null
              }
              briefActions={
                showBriefActions ? (
                  <VendorBriefActions
                    presentation="buttons"
                    snapshot={order.handoff}
                    orderId={order.id}
                    tenantId={tenantId}
                    tenantVendorId={order.tenantVendorId}
                    sentByUserName={sentByName}
                    viewer={activityViewer}
                  >
                    {(items) => items}
                  </VendorBriefActions>
                ) : null
              }
              reviewAction={
                activeScript &&
                !isMultiHookLine(activeScript) &&
                (activeScript.scriptReviewId || activeScript.preflightUrl) ? (
                  <ScriptReviewAction
                    scriptReviewId={activeScript.scriptReviewId}
                    preflightUrl={activeScript.preflightUrl}
                    label={t(
                      "marketplace.orders.list.reviewVideo",
                      "Review video",
                    )}
                    ariaLabel={t("marketplace.orders.list.reviewVideoAria", {
                      title,
                      defaultValue:
                        "Review video for {{title}} (opens in a new tab)",
                    })}
                    size="sm"
                    compact
                  />
                ) : null
              }
              meta={
                <>
                  {activeScript ? (
                    <>
                      <StatusMenu
                        value={activeScript.fulfillmentStatus}
                        options={fulfillmentOptionsFor(
                          order,
                          activeScript.fulfillmentStatus,
                        )}
                        editable={canEdit}
                        pending={pendingScriptIds.has(activeScript.id)}
                        ariaLabel={t(
                          "marketplace.orders.list.panel.statusEditor",
                        )}
                        onChange={(next) => handleStatus(activeScript.id, next)}
                      />
                      {revisionCount > 0 ? (
                        <Badge
                          variant="outline"
                          className="text-caption shrink-0 border-border-subtle font-normal tabular-nums text-muted-foreground"
                        >
                          {t("marketplace.orders.activity.revisionRound", {
                            count: revisionCount,
                          })}
                        </Badge>
                      ) : null}
                      <span className="inline-flex shrink-0">{dueEditor}</span>
                    </>
                  ) : null}
                  <Link
                    to="/marketplace"
                    search={{ tab: "vendors", vendor: order.vendor.id }}
                    aria-label={t(
                      "marketplace.orders.list.panel.vendorConnections",
                      { name: order.vendor.name },
                    )}
                    className="text-caption tracking-label min-w-0 truncate uppercase text-muted-foreground no-underline hover:text-foreground"
                  >
                    {order.vendor.name}
                  </Link>
                  {activeScript ? (
                    <>
                      <span aria-hidden className="shrink-0 text-muted-foreground">
                        ·
                      </span>
                      <span className="text-caption shrink-0 text-muted-foreground">
                        {t("marketplace.orders.list.panel.orderLabel")}{" "}
                        <CopyableId
                          value={order.id}
                          displayValue={shortOrderRef(order.id) ?? order.id}
                          density="quiet"
                          copyLabel={t("marketplace.orders.copyOrderId")}
                          copiedMessage={t(
                            "marketplace.orders.copyOrderIdSuccess",
                          )}
                          className="normal-case text-foreground"
                        />
                      </span>
                    </>
                  ) : null}
                  {activeScript && scriptIndex >= 0 ? (
                    <>
                      <span
                        aria-hidden
                        className="shrink-0 text-muted-foreground"
                      >
                        ·
                      </span>
                      <span className="text-caption shrink-0 tabular-nums text-muted-foreground">
                        {t("marketplace.orders.detail.scriptPosition", {
                          index: scriptIndex + 1,
                          total: order.scripts.length,
                        })}
                      </span>
                    </>
                  ) : null}
                  {order.scripts.length > 1 ? (
                    <span className="inline-flex shrink-0 items-center">
                      <PanelIconTooltip
                        label={t(
                          "marketplace.orders.list.panel.previousScript",
                        )}
                      >
                        <span className="inline-flex">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t(
                              "marketplace.orders.list.panel.previousScript",
                            )}
                            className="text-muted-foreground"
                            disabled={!previousTarget}
                            onClick={() => {
                              if (previousTarget)
                                onTargetChange(previousTarget);
                            }}
                          >
                            <Icon
                              name="chevron-left"
                              size="sm"
                              color="inherit"
                            />
                          </Button>
                        </span>
                      </PanelIconTooltip>
                      <PanelIconTooltip
                        label={t("marketplace.orders.list.panel.nextScript")}
                      >
                        <span className="inline-flex">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t(
                              "marketplace.orders.list.panel.nextScript",
                            )}
                            className="text-muted-foreground"
                            disabled={!nextTarget}
                            onClick={() => {
                              if (nextTarget) onTargetChange(nextTarget);
                            }}
                          >
                            <Icon
                              name="chevron-right"
                              size="sm"
                              color="inherit"
                            />
                          </Button>
                        </span>
                      </PanelIconTooltip>
                    </span>
                  ) : null}
                </>
              }
            />
          ) : null}
          {isHub ? null : (
          <>
          <div className="flex items-start justify-between gap-2">
            {activeScript ? (
              <div className="text-caption tracking-label flex min-w-0 items-center gap-2 text-muted-foreground">
                {tenant ? (
                  <>
                    <ClientMark
                      name={tenant.name}
                      logoUrl={tenant.logoUrl}
                      brandColor={tenant.brandColor}
                      size="sm"
                    />
                    <span className="truncate uppercase">{tenant.name}</span>
                    <span aria-hidden>·</span>
                  </>
                ) : null}
                <Link
                  to="/marketplace"
                  search={{ tab: "vendors", vendor: order.vendor.id }}
                  aria-label={t(
                    "marketplace.orders.list.panel.vendorConnections",
                    { name: order.vendor.name },
                  )}
                  className="truncate uppercase text-muted-foreground no-underline hover:text-foreground"
                >
                  {order.vendor.name}
                </Link>
                <span aria-hidden>·</span>
                <CopyableId
                  value={order.id}
                  displayValue={shortOrderRef(order.id) ?? order.id}
                  label={t("marketplace.orders.list.panel.orderLabel")}
                  copyLabel={t("marketplace.orders.copyOrderId")}
                  copiedMessage={t("marketplace.orders.copyOrderIdSuccess")}
                  truncate={false}
                />
              </div>
            ) : (
              <div className="flex min-w-0 flex-1 items-center gap-3">
                {tenant ? (
                  <ClientMark
                    name={tenant.name}
                    logoUrl={tenant.logoUrl}
                    brandColor={tenant.brandColor}
                  />
                ) : (
                  <VendorIdentity
                    name={order.vendor.name}
                    logoUrl={order.vendor.logoUrl}
                    className="size-9 text-xs"
                  />
                )}
                <div className="flex min-w-0 flex-col gap-0.5">
                  <SheetTitle className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 font-medium text-foreground">
                    <span>{t("marketplace.orders.list.panel.orderNoun")}</span>
                    <CopyableId
                      value={order.id}
                      displayValue={shortOrderRef(order.id) ?? order.id}
                      density="quiet"
                      copyLabel={t("marketplace.orders.copyOrderId")}
                      copiedMessage={t("marketplace.orders.copyOrderIdSuccess")}
                      className="text-[15px] font-medium text-foreground"
                    />
                  </SheetTitle>
                  <p className="text-caption min-w-0 truncate text-muted-foreground">
                    {tenant ? (
                      <>
                        <span>{tenant.name}</span>
                        <span aria-hidden> · </span>
                      </>
                    ) : null}
                    <Link
                      to="/marketplace"
                      search={{ tab: "vendors", vendor: order.vendor.id }}
                      aria-label={t(
                        "marketplace.orders.list.panel.vendorConnections",
                        { name: order.vendor.name },
                      )}
                      className="text-muted-foreground no-underline hover:text-foreground"
                    >
                      {order.vendor.name}
                    </Link>
                    <span aria-hidden> · </span>
                    {t("marketplace.orders.list.panel.headerScriptCount", {
                      count: order.scripts.length,
                    })}
                  </p>
                </div>
              </div>
            )}
            <div className="flex shrink-0 items-center gap-0.5">
              {activeScript && scriptIndex >= 0 ? (
                <span className="text-caption mr-1 tabular-nums text-muted-foreground">
                  {t("marketplace.orders.detail.scriptPosition", {
                    index: scriptIndex + 1,
                    total: order.scripts.length,
                  })}
                </span>
              ) : null}
              {order.scripts.length > 1 ? (
                <>
                  <PanelIconTooltip
                    label={t("marketplace.orders.list.panel.previousScript")}
                  >
                    <span className="inline-flex">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t(
                          "marketplace.orders.list.panel.previousScript",
                        )}
                        className="text-muted-foreground"
                        disabled={!previousTarget}
                        onClick={() => {
                          if (previousTarget) onTargetChange(previousTarget);
                        }}
                      >
                        <Icon name="chevron-left" size="sm" color="inherit" />
                      </Button>
                    </span>
                  </PanelIconTooltip>
                  <PanelIconTooltip
                    label={t("marketplace.orders.list.panel.nextScript")}
                  >
                    <span className="inline-flex">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t(
                          "marketplace.orders.list.panel.nextScript",
                        )}
                        className="text-muted-foreground"
                        disabled={!nextTarget}
                        onClick={() => {
                          if (nextTarget) onTargetChange(nextTarget);
                        }}
                      >
                        <Icon name="chevron-right" size="sm" color="inherit" />
                      </Button>
                    </span>
                  </PanelIconTooltip>
                </>
              ) : null}
              {hasMoreActions ? (
                <VendorBriefActions
                  snapshot={showBriefActions ? order.handoff : null}
                  orderId={order.id}
                  tenantId={tenantId}
                  tenantVendorId={order.tenantVendorId}
                  sentByUserName={sentByName}
                  viewer={activityViewer}
                >
                  {(briefItems) => (
                    <DropdownMenu>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              aria-label={t(
                                "marketplace.orders.list.panel.more",
                              )}
                              className="text-muted-foreground/70 hover:text-foreground"
                            >
                              <Icon
                                name="more-horizontal"
                                size="sm"
                                color="inherit"
                              />
                            </Button>
                          </DropdownMenuTrigger>
                        </TooltipTrigger>
                        <TooltipContent>
                          {t("marketplace.orders.list.panel.more")}
                        </TooltipContent>
                      </Tooltip>
                      <DropdownMenuContent align="end" className="min-w-44">
                        {briefItems}
                        {briefItems && hasPanelMenuActions ? (
                          <DropdownMenuSeparator />
                        ) : null}
                        {isScript ? (
                          <DropdownMenuItem
                            icon="file-text"
                            className="text-xs"
                            onSelect={() =>
                              onTargetChange({
                                kind: "order",
                                orderId: order.id,
                              })
                            }
                          >
                            {t("marketplace.orders.list.panel.openOrder")}
                          </DropdownMenuItem>
                        ) : null}
                        {canViewHistory ? (
                          <DropdownMenuItem
                            icon={viewHistoryIcon}
                            className="text-xs"
                            onSelect={onViewHistory}
                          >
                            {viewHistoryLabel}
                          </DropdownMenuItem>
                        ) : null}
                        {showCopyLinkAction ? (
                          <DropdownMenuItem
                            icon="link"
                            className="text-xs"
                            onSelect={copyLink}
                          >
                            {t("marketplace.orders.list.panel.copyLink")}
                          </DropdownMenuItem>
                        ) : null}
                        {canDuplicateActiveScript || canSendActiveScript ? (
                          <DropdownMenuSeparator />
                        ) : null}
                        {canDuplicateActiveScript ? (
                          <DropdownMenuItem
                            icon="copy"
                            className="text-xs"
                            disabled={duplicatePending}
                            onSelect={() => {
                              if (activeScript) onDuplicateScript?.(activeScript);
                            }}
                          >
                            {t(
                              "marketplace.orders.list.panel.duplicateScript",
                              "Duplicate script",
                            )}
                          </DropdownMenuItem>
                        ) : null}
                        {canSendActiveScript ? (
                          <DropdownMenuItem
                            icon="share"
                            className="text-xs"
                            onSelect={() => {
                              if (activeScript) onSendToPartner?.(activeScript);
                            }}
                          >
                            {t(
                              "marketplace.orders.list.panel.sendToPartner",
                              "Send to another partner…",
                            )}
                          </DropdownMenuItem>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </VendorBriefActions>
              ) : null}
              <PanelIconTooltip
                label={t("marketplace.orders.list.panel.close")}
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t("marketplace.orders.list.panel.close")}
                  className="text-muted-foreground"
                  onClick={onClose}
                >
                  <Icon name="x" size="sm" color="inherit" />
                </Button>
              </PanelIconTooltip>
            </div>
          </div>

          {activeScript ? (
            <ClampedTitle className="text-drawer-title">{title}</ClampedTitle>
          ) : null}

          {activeScript ? (
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                <StatusMenu
                  value={activeScript.fulfillmentStatus}
                  options={
                    statusOptionMode === "ops-admin"
                      ? fulfillmentOptionsForOpsAdminScript(order, activeScript)
                      : statusOptionsFor(order, activeScript.fulfillmentStatus)
                  }
                  editable={canEdit}
                  pending={pendingScriptIds.has(activeScript.id)}
                  ariaLabel={t("marketplace.orders.list.panel.statusEditor")}
                  onChange={(next) => handleStatus(activeScript.id, next)}
                />
                <OrderTypeBadge type={resolveOrderLineType(activeScript)} />
                {revisionCount > 0 ? (
                  <Badge
                    variant="outline"
                    className="text-caption border-border-subtle font-normal tabular-nums text-muted-foreground"
                  >
                    {t("marketplace.orders.activity.revisionRound", {
                      count: revisionCount,
                    })}
                  </Badge>
                ) : null}
                {dueEditor}
              </div>
              {!isMultiHookLine(activeScript) &&
              (activeScript.scriptReviewId || activeScript.preflightUrl) ? (
                <ScriptReviewAction
                  scriptReviewId={activeScript.scriptReviewId}
                  preflightUrl={activeScript.preflightUrl}
                  label={t(
                    "marketplace.orders.list.reviewVideo",
                    "Review video",
                  )}
                  ariaLabel={t("marketplace.orders.list.reviewVideoAria", {
                    title,
                    defaultValue:
                      "Review video for {{title}} (opens in a new tab)",
                  })}
                  className="ml-auto"
                />
              ) : null}
            </div>
          ) : null}
          </>
          )}
          {canViewActivity ? (
            <Tabs
              value={activeTab}
              onValueChange={(value) => {
                if (value === "content" || value === "activity") {
                  setActiveTab(value);
                }
              }}
              className="-mb-px gap-0"
            >
              <TabsList
                variant="line"
                aria-label={t(
                  "marketplace.orders.list.panel.tabs",
                  "Drawer sections",
                )}
                className="h-auto min-h-11 w-full justify-start border-b-0"
                options={[
                  {
                    value: "content",
                    label: activeScript
                      ? t("marketplace.orders.list.panel.scriptTab", "Script")
                      : t("marketplace.orders.list.panel.orderTab", "Order"),
                  },
                  {
                    value: "activity",
                    label: t(
                      "marketplace.orders.list.panel.activityTab",
                      "Activity",
                    ),
                    badge: activityLoading ? undefined : activity.length,
                  },
                ]}
              />
            </Tabs>
          ) : null}
        </header>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {activeTab === "activity" ? activitySection : null}
            <div className={activeTab === "activity" ? "hidden" : undefined}>
              {activeScript && display ? (
                <>
                <div
                  className={cn(
                    isHub
                      ? "grid items-start gap-x-10 gap-y-8 lg:grid-cols-[minmax(0,1.4fr)_minmax(16rem,0.7fr)]"
                      : "flex min-h-full flex-col gap-8",
                  )}
                >
                  <div
                    className={cn(
                      isHub ? "flex min-w-0 flex-col gap-8" : "contents",
                    )}
                  >
                  <div className="flex flex-col gap-3">
                    {display.hook ? (
                      <ContentSection
                        wide={isHub}
                        title={t("marketplace.orders.list.drawer.hook")}
                      >
                        {display.hook}
                      </ContentSection>
                    ) : null}
                    {display.synopsis ? (
                      <ContentSection
                        wide={isHub}
                        title={t("marketplace.orders.list.drawer.synopsis")}
                      >
                        {display.synopsis}
                      </ContentSection>
                    ) : null}
                  </div>

                  <OrderLineHookVideos script={activeScript} title={title} />

                  {display.personaBrief ? (
                    <OrderPersonaBriefSection snapshot={display.personaBrief} />
                  ) : null}

                  {isHub ? null : (
                  <section className="flex flex-col gap-3 border-y border-border-subtle py-3">
                    <h3 className="text-caption tracking-label font-medium uppercase text-muted-foreground">
                      {t("marketplace.orders.list.panel.sourceContext")}
                    </h3>
                    <DetailMetaGrid
                      compact
                      items={[
                        {
                          label: sourceFieldLabel,
                          value: sourceLabel,
                        },
                        {
                          label: t("marketplace.orders.list.panel.persona"),
                          value: linkedPersona ? null : display.personaName,
                        },
                        {
                          label: t(
                            "marketplace.orders.list.panel.opportunitySet",
                          ),
                          value: opportunitySetLink,
                        },
                        {
                          label: t("marketplace.orders.list.panel.opportunity"),
                          value: opportunityLabel,
                        },
                        {
                          label: t("marketplace.orders.list.panel.format"),
                          value: display.format
                            ? display.duration
                              ? t(
                                  "marketplace.orders.list.panel.formatAndDuration",
                                  {
                                    format: display.format,
                                    duration: display.duration,
                                  },
                                )
                              : display.format
                            : display.duration,
                        },
                      ]}
                    />
                  </section>
                  )}

                  {(display.handoffFields.length > 0 ||
                    display.orderInstructions ||
                    order.etaNote?.trim() ||
                    activeScript.revisionNote) && (
                    <div className="flex flex-col gap-3">
                      {display.handoffFields.map((field) => (
                        <ContentSection
                          key={field.key}
                          wide={isHub}
                          title={t(
                            `marketplace.handoff.fields.${field.key}.label`,
                          )}
                        >
                          {field.value}
                        </ContentSection>
                      ))}
                      {display.orderInstructions ? (
                        <ContentSection
                          wide={isHub}
                          title={t("marketplace.sendHandoff.orderNoteTitle")}
                        >
                          {display.orderInstructions}
                        </ContentSection>
                      ) : null}
                      {order.etaNote?.trim() ? (
                        <ContentSection
                          wide={isHub}
                          title={t("marketplace.orders.list.etaNote")}
                        >
                          {order.etaNote}
                        </ContentSection>
                      ) : null}
                      {activeScript.revisionNote ? (
                        <section className="rounded-md border border-warning-border bg-warning-subtle px-3 py-3">
                          <h3 className="text-caption tracking-label font-medium uppercase text-warning-subtle-foreground">
                            {t("marketplace.orders.list.revisionNote")}
                          </h3>
                          <p className="text-drawer-body mt-1 text-warning-subtle-foreground">
                            {activeScript.revisionNote}
                          </p>
                        </section>
                      ) : null}
                    </div>
                  )}

                  {isHub ? null : (
                  <DetailMetaGrid
                    compact
                    items={[
                      {
                        label: t("marketplace.orders.list.columns.sent"),
                        value: sentLabel,
                      },
                      ...(sentByName
                        ? [
                            {
                              label: t(
                                "marketplace.orders.list.columns.sentBy",
                                "Sent by",
                              ),
                              value: sentByValue,
                            },
                          ]
                        : []),
                    ]}
                  />
                  )}

                  {display.fullScript ? (
                    <ContentSection
                      wide={isHub}
                      title={t("marketplace.orders.list.panel.fullScript")}
                    >
                      {display.fullScript}
                    </ContentSection>
                  ) : null}
                  {isHub ? null : (
                    <OrderScriptStoryboards script={activeScript} title={title} />
                  )}
                  {!isHub &&
                  (display.briefUrl ||
                    display.uploadId ||
                    activeScript.scriptReviewId ||
                    activeScript.preflightUrl) ? (
                    <section className="flex flex-col gap-1">
                      <h3 className="text-caption tracking-label font-medium uppercase text-muted-foreground">
                        {t("marketplace.orders.list.panel.deliverables")}
                      </h3>
                      <OrderScriptRowActions
                        uploadId={display.uploadId}
                        downloadLabel={
                          activeScript.customAssetKind === "BRIEF"
                            ? "brief"
                            : "script"
                        }
                        onDownloadAsset={
                          display.uploadId && onDownloadAsset
                            ? () => onDownloadAsset(activeScript)
                            : undefined
                        }
                        className="flex-row justify-start"
                      />
                      <ScriptActionButtons
                        briefUrl={display.briefUrl}
                        hasUpload={false}
                        scriptReviewId={
                          display.briefOnly ? null : activeScript.scriptReviewId
                        }
                        preflightUrl={
                          display.briefOnly ? null : activeScript.preflightUrl
                        }
                        sourceKind={
                          activeScript.customAssetKind === "BRIEF"
                            ? "brief"
                            : "script"
                        }
                        onDownloadAsset={
                          onDownloadAsset
                            ? () => onDownloadAsset(activeScript)
                            : undefined
                        }
                        className="justify-start opacity-100"
                        showReview={false}
                      />
                    </section>
                  ) : null}
                  </div>
                  {isHub ? (
                    <div className="flex min-w-0 flex-col gap-6">
                      <section className="flex flex-col gap-3">
                        <h3 className="text-caption tracking-label font-medium uppercase text-muted-foreground">
                          {t("marketplace.orders.list.panel.sourceContext")}
                        </h3>
                        <DetailMetaGrid
                          className="grid-cols-1 sm:grid-cols-1"
                          items={[
                            { label: sourceFieldLabel, value: sourceLabel },
                            {
                              label: t("marketplace.orders.list.panel.persona"),
                              value: linkedPersona ? null : display.personaName,
                            },
                            {
                              label: t(
                                "marketplace.orders.list.panel.opportunitySet",
                              ),
                              value: opportunitySetLink,
                            },
                            {
                              label: t("marketplace.orders.list.panel.opportunity"),
                              value: opportunityLabel,
                            },
                            {
                              label: t("marketplace.orders.list.panel.format"),
                              value: display.format
                                ? display.duration
                                  ? t(
                                      "marketplace.orders.list.panel.formatAndDuration",
                                      {
                                        format: display.format,
                                        duration: display.duration,
                                      },
                                    )
                                  : display.format
                                : display.duration,
                            },
                          ]}
                        />
                      </section>
                      <DetailMetaGrid
                        className="grid-cols-1 sm:grid-cols-1"
                        items={[
                          {
                            label: t("marketplace.orders.list.columns.sent"),
                            value: sentLabel,
                          },
                          ...(sentByName
                            ? [
                                {
                                  label: t(
                                    "marketplace.orders.list.columns.sentBy",
                                    "Sent by",
                                  ),
                                  value: sentByValue,
                                },
                              ]
                            : []),
                        ]}
                      />
                      {display.briefUrl ||
                      display.uploadId ||
                      activeScript.scriptReviewId ||
                      activeScript.preflightUrl ? (
                        <section className="flex flex-col gap-1">
                          <h3 className="text-caption tracking-label font-medium uppercase text-muted-foreground">
                            {t("marketplace.orders.list.panel.deliverables")}
                          </h3>
                          <OrderScriptRowActions
                            uploadId={display.uploadId}
                            downloadLabel={
                              activeScript.customAssetKind === "BRIEF"
                                ? "brief"
                                : "script"
                            }
                            onDownloadAsset={
                              display.uploadId && onDownloadAsset
                                ? () => onDownloadAsset(activeScript)
                                : undefined
                            }
                            className="flex-row justify-start"
                          />
                          <ScriptActionButtons
                            briefUrl={display.briefUrl}
                            hasUpload={false}
                            scriptReviewId={
                              display.briefOnly ? null : activeScript.scriptReviewId
                            }
                            preflightUrl={
                              display.briefOnly ? null : activeScript.preflightUrl
                            }
                            sourceKind={
                              activeScript.customAssetKind === "BRIEF"
                                ? "brief"
                                : "script"
                            }
                            onDownloadAsset={
                              onDownloadAsset
                                ? () => onDownloadAsset(activeScript)
                                : undefined
                            }
                            className="justify-start opacity-100"
                            showReview={false}
                          />
                        </section>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {isHub ? (
                  <OrderScriptStoryboards
                    script={activeScript}
                    title={title}
                    className="mt-8"
                  />
                ) : null}
                </>
              ) : (
                <div
                  className={cn(
                    isHub
                      ? "grid items-start gap-x-10 gap-y-8 lg:grid-cols-[minmax(0,36rem)_16rem]"
                      : "flex min-h-full flex-col gap-5",
                  )}
                >
                  <div
                    className={cn(
                      isHub ? "flex min-w-0 flex-col gap-5" : "contents",
                    )}
                  >
                  {isHub ? null : (
                  <DetailMetaGrid
                    items={[
                      {
                        label: t("marketplace.orders.list.columns.sent"),
                        value: sentLabel,
                      },
                      ...(sentByName
                        ? [
                            {
                              label: t(
                                "marketplace.orders.list.columns.sentBy",
                                "Sent by",
                              ),
                              value: sentByValue,
                            },
                          ]
                        : []),
                      {
                        label: t("marketplace.orders.list.columns.due"),
                        value: canEditDue ? dueEditor : dueCombined,
                      },
                      {
                        label: t("marketplace.orders.list.panel.orderLabel"),
                        value: (
                          <CopyableId
                            value={order.id}
                            displayValue={shortOrderRef(order.id) ?? order.id}
                            copyLabel={t("marketplace.orders.copyOrderId")}
                            copiedMessage={t(
                              "marketplace.orders.copyOrderIdSuccess",
                            )}
                          />
                        ),
                      },
                      {
                        label: t(
                          "marketplace.orders.list.panel.scriptsCountLabel",
                        ),
                        value: order.scripts.length,
                      },
                      ...buildOrderTargetRatioMetaItems(
                        order.handoff?.targetRatios,
                        order.scripts.length,
                        t,
                      ),
                    ]}
                  />
                  )}
                  {order.etaNote?.trim() ? (
                    <ContentSection
                      title={t("marketplace.orders.list.etaNote")}
                    >
                      {order.etaNote}
                    </ContentSection>
                  ) : null}
                  <section
                    data-orders-section="scripts"
                    className="flex flex-col gap-2"
                  >
                    <h3 className="text-drawer-body font-medium text-foreground">
                      {t("marketplace.orders.list.panel.scriptsHeading", {
                        count: order.scripts.length,
                      })}
                    </h3>
                    <ul className="divide-y divide-border-subtle overflow-hidden rounded-md border border-border">
                      {order.scripts.map((row, index) => {
                        const rowDisplay = describeOrderScript(
                          row,
                          t("marketplace.orders.detail.unnamedScript"),
                        );
                        return (
                          <li
                            key={row.id}
                            className="flex flex-col gap-1.5 px-3 py-2.5 transition-colors duration-150 ease-out hover:bg-muted/40 motion-reduce:transition-none"
                          >
                            <div className="flex min-w-0 items-start gap-1">
                              <button
                                type="button"
                                className="flex min-w-0 flex-1 cursor-pointer items-start gap-3 text-left"
                                onClick={() =>
                                  onTargetChange({
                                    kind: "script",
                                    orderId: order.id,
                                    scriptId: row.id,
                                  })
                                }
                              >
                                <span
                                  aria-hidden
                                  className="w-4 shrink-0 text-xs tabular-nums leading-5 text-muted-foreground"
                                >
                                  {index + 1}
                                </span>
                                <span className="min-w-0 flex-1 text-[13px] font-medium leading-snug text-foreground [overflow-wrap:anywhere] line-clamp-2">
                                  {rowDisplay.title}
                                </span>
                              </button>
                              <div className="flex shrink-0 items-center gap-1">
                                <OrderScriptRowActions
                                  uploadId={rowDisplay.uploadId}
                                  downloadLabel={
                                    row.customAssetKind === "BRIEF"
                                      ? "brief"
                                      : "script"
                                  }
                                  onDownloadAsset={
                                    rowDisplay.uploadId && onDownloadAsset
                                      ? () => onDownloadAsset(row)
                                      : undefined
                                  }
                                  className="flex-row"
                                />
                                {(!rowDisplay.briefOnly || canReviewBriefLines) &&
                                canShowOrderLineReview(row) &&
                                (row.scriptReviewId || row.preflightUrl) ? (
                                  <ScriptReviewAction
                                    scriptReviewId={row.scriptReviewId}
                                    preflightUrl={row.preflightUrl}
                                    label={t(
                                      "marketplace.orders.list.review",
                                      "Review",
                                    )}
                                    ariaLabel={t(
                                      "marketplace.orders.list.reviewVideoAria",
                                      {
                                        title: rowDisplay.title,
                                        defaultValue:
                                          "Review video for {{title}} (opens in a new tab)",
                                      },
                                    )}
                                    variant="default"
                                    size="sm"
                                    compact
                                  />
                                ) : null}
                              </div>
                            </div>
                            <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 pl-7">
                              <StatusMenu
                                value={row.fulfillmentStatus}
                                options={
                                  statusOptionMode === "ops-admin"
                                    ? fulfillmentOptionsForOpsAdminScript(
                                        order,
                                        row,
                                      )
                                    : statusOptionsFor(order, row.fulfillmentStatus)
                                }
                                editable={canEdit}
                                pending={pendingScriptIds.has(row.id)}
                                ariaLabel={t(
                                  "marketplace.orders.list.statusMenuLabel",
                                  { index: index + 1 },
                                )}
                                className="max-w-full"
                                onChange={(next) => handleStatus(row.id, next)}
                              />
                              <OrderTypeBadge type={resolveOrderLineType(row)} />
                              <DeadlineChip
                                ymd={row.expectedDeliveryDate}
                                todayYmd={todayYmd}
                                muted
                              />
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                  {order.scripts.map((row) => {
                    const rowTitle = describeOrderScript(
                      row,
                      t("marketplace.orders.detail.unnamedScript"),
                    ).title;
                    return (
                      <OrderScriptStoryboards
                        key={row.id}
                        script={row}
                        title={rowTitle}
                        heading={t(
                          "marketplace.orders.list.panel.storyboardForScript",
                          {
                            title: rowTitle,
                            defaultValue: "Storyboard · {{title}}",
                          },
                        )}
                      />
                    );
                  })}
                  <VendorBriefSection
                    snapshot={order.handoff}
                    tenantId={tenantId}
                    tenantVendorId={order.tenantVendorId}
                    scripts={order.scripts}
                  />
                  </div>
                  {isHub ? (
                    <DetailMetaGrid
                      className="grid-cols-1 sm:grid-cols-1"
                      items={[
                        {
                          label: t("marketplace.orders.list.columns.sent"),
                          value: sentLabel,
                        },
                        ...(sentByName
                          ? [
                              {
                                label: t(
                                  "marketplace.orders.list.columns.sentBy",
                                  "Sent by",
                                ),
                                value: sentByValue,
                              },
                            ]
                          : []),
                        {
                          label: t("marketplace.orders.list.columns.due"),
                          value: canEditDue ? dueEditor : dueCombined,
                        },
                        {
                          label: t(
                            "marketplace.orders.list.panel.scriptsCountLabel",
                          ),
                          value: order.scripts.length,
                        },
                        ...buildOrderTargetRatioMetaItems(
                          order.handoff?.targetRatios,
                          order.scripts.length,
                          t,
                        ),
                      ]}
                    />
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {canAddComment && (activeScript || order.scripts[0]) ? (
            <DetailCommentComposer
              pending={addComment.isPending}
              onSubmit={async (body) => {
                const scriptId = activeScript?.id ?? order.scripts[0]?.id;
                if (!scriptId) return;
                await addComment.mutateAsync({
                  orderId: order.id,
                  orderScriptId: scriptId,
                  tenantId,
                  body,
                  requestRevision: false,
                });
              }}
            />
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
